import { lazy, type ComponentType } from "react";

/**
 * React.lazy met herstel voor "Failed to fetch dynamically imported module".
 *
 * Na een nieuwe deploy krijgen bestaande browser-tabs (of een service worker
 * met een oude index.html) chunknamen te pakken die niet meer bestaan.
 * Het gevolg: een lege/foutpagina op bijv. een geboostte vacature-link.
 *
 * Strategie:
 *  1. Eén retry met cache-busting querystring.
 *  2. Lukt dat ook niet, dan één keer hard herladen (max 1x per sessie per
 *     chunk, zodat we nooit in een reload-loop komen).
 */
const RELOAD_KEY = "cw:chunk-reload:";

export function lazyWithRetry<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
  key?: string,
) {
  return lazy(async () => {
    try {
      return await factory();
    } catch (err) {
      // 1) Retry — vaak is het een tijdelijke netwerkhikkup.
      try {
        await new Promise((r) => setTimeout(r, 400));
        return await factory();
      } catch (err2) {
        // 2) Nieuwe build → oude chunknaam. Eenmalig hard herladen.
        const storageKey = RELOAD_KEY + (key || factory.toString().slice(0, 80));
        try {
          if (typeof sessionStorage !== "undefined" && !sessionStorage.getItem(storageKey)) {
            sessionStorage.setItem(storageKey, "1");
            window.location.reload();
            // Houd de Suspense hangend tot de reload doorkomt.
            return await new Promise<{ default: T }>(() => {});
          }
        } catch {
          /* sessionStorage geblokkeerd — val door naar de fout */
        }
        throw err2;
      }
    }
  });
}
